import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, BookOpen, Users, Mic, Award, Linkedin, Download } from 'lucide-react';
import { speakingTopics, testimonials, audienceTypes, whyBookRavi, bookInfo } from '../mockData';
import SEO from '../components/SEO';

const Home = () => {
  const [currentHeroImage, setCurrentHeroImage] = React.useState(0);
  
  // 5 rotating hero background images - professional speaking photos
  const heroImages = [
    'https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/siavopmj__DSC3915.jpg', // Auditorium
    'https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/6qxmtcum__DSC3776.jpg', // Safar Book Stack
    'https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/facddnzs__DSC3877.jpg', // Seated on Suitcase
    'https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/c84otrlr__DSC3978.jpg', // Ladder + Suitcase
    'https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/ytrxv2l6__DSC3828.jpg'  // Waving with Blue Suitcase
  ];
  
  // Individual background positions for each image to ensure head is visible
  const heroPositions = [
    'center 35%',  // Auditorium - you're centered
    'center 40%',  // Book Stack - books in foreground
    'center 25%',  // Seated on Suitcase - position higher to show head
    'center 20%',  // Ladder + Suitcase - position higher to show head
    'center 30%'   // Waving - centered position
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 7000); // 7 seconds per image

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Keynote Speaker & Leadership Expert"
        description="Ravi Prakash is a TEDx speaker, bestselling author of Safar, and leadership expert. Book Ravi for keynotes on resilience, reinvention, and staying human in the age of AI. 59K+ TEDx views."
        canonical="/"
      />

      {/* Hero Section with Auto-Rotating Background */}
      <section className="hero-section relative bg-near-black text-white overflow-hidden">
        {/* Auto-rotating background images */}
        {heroImages.map((image, index) => (
          <div
            key={`hero-${image}`}
            className={`absolute inset-0 bg-cover ${
              index === currentHeroImage ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url('${image}')`,
              backgroundPosition: heroPositions[index],
              transition: 'opacity 3000ms ease-in-out'
            }}
          ></div>
        ))}
        
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/85 via-gray-900/75 to-gray-900/85"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-xl text-white mb-6">
            Keynotes on leadership, resilience, and reinvention for people facing real change
          </h1>
          <p className="text-xl md:text-2xl font-sans text-gray-200 mb-8 max-w-4xl mx-auto leading-relaxed">
            Ravi Prakash helps organizations and emerging leaders navigate pressure, change, and identity through honest, practical, human-centered keynote talks.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary text-lg px-8 py-4"
            >
              Book Ravi
            </a>
            <Link to="/speaking" className="btn-secondary text-lg px-8 py-4">
              View Speaking Topics
            </Link>
          </div>
          
          <p className="text-sm text-gray-400 font-sans">
            Available for keynotes, fireside chats, panels, graduations, conferences, and book signing events<br />
            In-person and virtual
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-sm font-sans font-semibold text-safar-blue uppercase tracking-wider mb-4">
              Trusted by leaders, teams, and audiences seeking substance—not just stage energy
            </p>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg md:text-xl font-serif text-charcoal leading-relaxed">
                <strong>TEDx speaker. Bestselling author. Global executive.</strong><br />
                Ravi brings lived experience, leadership perspective, and a message that resonates with audiences facing change, pressure, growth, and reinvention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Ravi Section */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Why audiences connect with Ravi
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              Ravi Prakash does not speak about resilience as a slogan. He speaks about it as lived reality.
            </p>
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              He came to the United States with $20, built a global career across industries and countries, and learned firsthand that leadership, reinvention, and growth are rarely neat, linear, or comfortable.
            </p>
            <p className="text-lg font-sans text-gray-800 leading-relaxed">
              Today, he brings that perspective to the stage with a message that is honest, grounded, and deeply human.
            </p>
            
            <div className="mt-8">
              <p className="text-lg font-sans font-semibold text-charcoal mb-4">His talks help audiences:</p>
              <ul className="space-y-3">
                {[
                  'Lead under pressure',
                  'Navigate uncertainty with courage',
                  'Rethink identity during change',
                  'Stay human in the age of AI',
                  'Move forward with clarity, conviction, and self-respect'
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-safar-blue mr-3 flex-shrink-0 mt-1" />
                    <span className="text-lg font-sans text-gray-800">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="text-center mt-10">
              <Link to="/speaking" className="btn-primary inline-flex items-center">
                See Speaking Topics
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Watch Ravi Speak Section */}
      <section className="bg-white section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-charcoal mb-4">
              See Ravi in Action
            </h2>
            <p className="text-lg font-sans text-gray-700 max-w-3xl mx-auto">
              From TEDx stages to corporate events, see the message that moves audiences.
            </p>
          </div>

          {/* Featured TEDx Video */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-lg overflow-hidden shadow-2xl bg-black">
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/ULNDVkxUxOc"
                  title="TEDx Talk: Self-Doubt to Confidence"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
            </div>
            <div className="text-center mt-4">
              <h3 className="text-2xl font-serif font-semibold text-charcoal mb-2">
                TEDx Talk: Self-Doubt to Confidence
              </h3>
              <p className="text-base font-sans text-gray-700 mb-2">
                TEDx Lake Mary
              </p>
              <p className="text-sm font-sans font-semibold text-safar-blue">
                >59K views
              </p>
            </div>
          </div>

          {/* Additional Videos Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Podcast Video */}
            <div>
              <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/N31Um6aBMks"
                    title="Dare to Declare Podcast"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  ></iframe>
                </div>
              </div>
              <div className="mt-4">
                <h4 className="text-xl font-serif font-semibold text-charcoal mb-2">
                  224 Rejections and One Yes
                </h4>
                <p className="text-sm font-sans text-gray-700">
                  Dare to Declare Podcast with Debbi Sluys
                </p>
              </div>
            </div>

            {/* Ramaiah Institute Talk */}
            <div>
              <div className="relative rounded-lg overflow-hidden shadow-lg bg-black">
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/WQO9Uye1L3E"
                    title="Leadership Talk at Ramaiah Institute"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  ></iframe>
                </div>
              </div>
              <div className="mt-4">
                <h4 className="text-xl font-serif font-semibold text-charcoal mb-2">
                  Leading Yourself: Life & Goal Setting
                </h4>
                <p className="text-sm font-sans text-gray-700">
                  Ramaiah Medical Institute, Bangalore, India
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center"
            >
              Bring This Energy to Your Event
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Video Shorts Section */}
      <section className="bg-white section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-charcoal mb-4">
              Quick Insights from Ravi
            </h2>
            <p className="text-lg font-sans text-gray-700 max-w-3xl mx-auto">
              Powerful leadership lessons in short video clips. Watch these featured insights and explore all 8 videos in the Media Kit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Video 1: Success Secrets */}
            <div className="bg-soft-white rounded-lg overflow-hidden shadow-lg border border-gray-200">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/Z5q6ZOsEirk"
                  title="Success Secrets: Transform Failure into Your Fuel"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h4 className="text-xl font-serif font-semibold text-charcoal mb-2">
                  Success Secrets: Transform Failure into Your Fuel
                </h4>
                <p className="text-sm font-sans text-gray-700 mb-4">
                  Learn how to turn setbacks into stepping stones for success
                </p>
              </div>
            </div>

            {/* Video 2: Kindness Changes Lives */}
            <div className="bg-soft-white rounded-lg overflow-hidden shadow-lg border border-gray-200">
              <div className="relative bg-black aspect-[9/16]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/1tFRoyAgebc"
                  title="Kindness Changes Lives: From Struggle to Success"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h4 className="text-xl font-serif font-semibold text-charcoal mb-2">
                  Kindness Changes Lives: From Struggle to Success
                </h4>
                <p className="text-sm font-sans text-gray-700 mb-4">
                  The power of human-centered leadership in action
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/media-kit" className="btn-secondary inline-flex items-center">
              View All 8 Video Clips in Media Kit
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Speaking Topics Preview */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-charcoal mb-4">
              Signature keynote topics
            </h2>
            <p className="text-lg font-sans text-gray-700 max-w-3xl mx-auto">
              Built for organizations, conferences, universities, and leadership events that want more than inspiration. These talks are designed to leave people thinking, feeling, and acting differently.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {speakingTopics.map((topic) => (
              <div
                key={topic.id}
                className="bg-soft-white border border-gray-200 rounded-lg p-8 card-hover"
              >
                <h3 className="text-2xl font-serif font-semibold text-charcoal mb-4">
                  {topic.title}
                </h3>
                <p className="text-base font-sans text-gray-700 mb-4 leading-relaxed">
                  {topic.shortDescription}
                </p>
                <p className="text-sm font-sans font-medium text-safar-blue">
                  Best for: {topic.bestFor}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/speaking" className="btn-primary inline-flex items-center">
              Explore All Speaking Topics
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Audience Fit Section */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-8">
            Who Ravi speaks to
          </h2>
          
          <p className="text-lg font-sans text-gray-800 text-center mb-10 max-w-3xl mx-auto">
            Ravi speaks to audiences that are dealing with growth, transition, pressure, or uncertainty, including:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audienceTypes.map((audience) => (
              <div
                key={audience}
                className="bg-white rounded-lg p-6 shadow-sm flex items-start space-x-3"
              >
                <Users className="h-6 w-6 text-safar-orange flex-shrink-0 mt-1" />
                <p className="text-base font-sans text-gray-800">{audience}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-base font-sans text-gray-600 mt-10 italic">
            If your audience is dealing with change, pressure, reinvention, growth, or identity, this message will land.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-charcoal mb-4">
              What people are saying
            </h2>
            <p className="text-lg font-sans text-gray-700 max-w-2xl mx-auto">
              Ravi's talks and book are remembered not just for the message, but for what they unlock in people.
            </p>
          </div>
          
          {/* Static 3-Testimonial Layout */}
          <div className="space-y-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-soft-white border border-gray-200 rounded-lg p-8 md:p-10"
              >
                <p className="text-lg md:text-xl font-sans text-gray-800 italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-gray-300 pt-4">
                  <p className="font-sans font-semibold text-charcoal text-base">
                    {testimonial.author}
                  </p>
                  <p className="text-sm font-sans text-gray-600">
                    {testimonial.title}, {testimonial.organization}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary text-lg px-8 py-4"
            >
              Book Ravi for Your Event
            </a>
          </div>
        </div>
      </section>

      {/* Why Book Ravi */}
      <section className="bg-soft-stone section-padding">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="heading-lg text-charcoal text-center mb-12">
            Why event organizers book Ravi
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            {whyBookRavi.map((reason) => (
              <div key={reason.title} className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Award className="h-8 w-8 text-safar-orange" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-semibold text-charcoal mb-2">
                    {reason.title}
                  </h3>
                  <p className="text-base font-sans text-gray-700">
                    {reason.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/media-kit" className="btn-secondary inline-flex items-center">
              Download Media Kit
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Book Section */}
      <section className="bg-white section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Newsletter CTA Banner */}
          <div className="bg-safar-blue rounded-2xl p-8 md:p-12 text-center mb-16 shadow-xl border-4 border-safar-orange">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <h3 className="text-5xl md:text-6xl font-serif font-bold text-safar-orange mb-3 drop-shadow-lg">
                  Leadership
                </h3>
                <h3 className="text-4xl md:text-5xl font-serif font-bold text-white mb-2 drop-shadow-md">
                  JOURNEYS WITH
                </h3>
                <h3 className="text-4xl md:text-5xl font-serif font-bold text-white mb-3 drop-shadow-md">
                  RAVI
                </h3>
                <div className="w-48 h-1.5 bg-safar-orange mx-auto mt-4 mb-3"></div>
                <p className="text-2xl font-serif text-white mt-3 font-semibold">Newsletter</p>
              </div>
              <p className="text-xl md:text-2xl font-sans text-white font-semibold mb-3">
                Weekly insights on leadership, resilience, and reinvention
              </p>
              <p className="text-lg font-sans text-white font-medium mb-6">
                Join <strong className="text-safar-orange">1,500+ leaders</strong> reading this LinkedIn newsletter every week
              </p>
              <a
                href="https://www.linkedin.com/newsletters/journey-with-ravi-7327477739354009600"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-safar-orange text-white hover:bg-burnt-orange font-sans font-bold px-10 py-5 rounded-lg transition-all transform hover:scale-105 shadow-2xl text-lg"
              >
                <Linkedin className="h-6 w-6 mr-3" />
                Subscribe to Newsletter
              </a>
              <p className="text-base text-white mt-5 font-sans font-semibold">
                Free • Weekly • Delivered on LinkedIn
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Book Image */}
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_leadership-change-1/artifacts/aqlqzcjk_BookCoverMockup.png"
                alt="Safar Book Cover"
                className="w-full max-w-md rounded-lg shadow-2xl"
              />
            </div>
            
            {/* Book Content */}
            <div>
              <h2 className="heading-md text-charcoal mb-4">
                Bring the message beyond the stage
              </h2>
              <h3 className="text-2xl font-serif font-semibold text-safar-blue mb-6">
                Safar: An Immigrant's Journey of Life & Leadership
              </h3>
              
              <p className="text-lg font-sans text-gray-800 mb-6 leading-relaxed">
                {bookInfo.description}
              </p>
              
              <div className="mb-8">
                <p className="font-sans font-semibold text-charcoal mb-3">Ideal for:</p>
                <ul className="space-y-2">
                  {bookInfo.idealFor.map((item) => (
                    <li key={item} className="flex items-start">
                      <BookOpen className="h-5 w-5 text-safar-orange mr-3 flex-shrink-0 mt-1" />
                      <span className="text-base font-sans text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={bookInfo.amazonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-center"
                >
                  Buy the Book
                </a>
                <a 
                  href="https://calendly.com/journeywithravi/30" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary text-center"
                >
                  Book a Signing Event
                </a>
              </div>
              
              <p className="text-sm font-sans text-gray-600 mt-4">
                Available for author talks, book signings, and event bundles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-safar-blue text-white section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-lg text-white mb-6">
            Planning an event that needs more than recycled motivation?
          </h2>
          <p className="text-xl font-sans text-gray-100 mb-10 leading-relaxed">
            If you are looking for a keynote speaker who brings lived experience, executive credibility, emotional resonance, and practical insight, let's talk.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://calendly.com/journeywithravi/30" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-safar-blue hover:bg-gray-100 font-sans font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Book Ravi
            </a>
            <Link to="/media-kit" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-safar-blue font-sans font-semibold px-8 py-4 rounded-lg transition-colors">
              Download Media Kit
            </Link>
          </div>
          
          <p className="text-sm text-gray-200 mt-6 font-sans">
            For conferences, corporate events, universities, retreats, panels, graduations, and book signing events.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
